/**
 * @fileoverview Capa de Datos (Data Layer - DL) para la dinámica 'Rasca y Gana - Optimo Ópticas' (Tile ID 49).
 * Proporciona llamadas HTTP fetch nativas desacopladas para interactuar con los endpoints REST del backend.
 * 
 * @module utilities/DL/OptimoOpticas
 */

const API_URL = process.env.REACT_APP_API_URL;

// =============================================================================
// ENDPOINTS PÚBLICOS DEL AFICIONADO
// =============================================================================

/**
 * Consulta la configuración general y estado operativo de la dinámica.
 * GET /api/OptimoOpticas/config
 * 
 * @async
 * @function GetPublicConfig
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
const GetPublicConfig = async () => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/config`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al obtener la configuración de la dinámica.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en GetPublicConfig (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al consultar configuración.' };
    }
};

/**
 * Comprueba si ya existe un participante registrado con el correo o teléfono dado.
 * POST /api/OptimoOpticas/check-duplicate
 * 
 * @async
 * @function CheckDuplicate
 * @param {string} phone - Teléfono normalizado del aficionado.
 * @param {string} email - Correo electrónico normalizado.
 * @returns {Promise<{success: boolean, exists?: boolean, message?: string, isDuplicate?: boolean, hasCoupon?: boolean, couponCode?: string}>}
 */
const CheckDuplicate = async (phone, email) => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/check-duplicate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phone, email })
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al comprobar duplicado de participante.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en CheckDuplicate (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al comprobar duplicado.' };
    }
};

/**
 * Procesa el registro y participación del aficionado de forma atómica e idempotente.
 * POST /api/OptimoOpticas/submit
 * 
 * @async
 * @function SubmitLead
 * @param {Object} payload - Objeto de participación { name, email, phone, acceptedTerms, acceptedMarketing, hasScratched, scratchedPercentage, trafficSource, sessionId }
 * @returns {Promise<{success: boolean, isWinner?: boolean, winnerRank?: number, couponCode?: string, verificationToken?: string, discountDescription?: string, message?: string}>}
 */
const SubmitLead = async (payload) => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al procesar la participación.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en SubmitLead (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al enviar registro.' };
    }
};

/**
 * Consulta y recupera el cupón del aficionado con control de Rate Limiting.
 * POST /api/OptimoOpticas/recover-coupon
 * 
 * @async
 * @function RecoverCoupon
 * @param {string} query - Correo electrónico o teléfono registrado.
 * @param {string|null} [searchType=null] - Tipo opcional de búsqueda ('EMAIL' | 'PHONE').
 * @param {string|null} [sessionId=null] - Identificador de sesión para telemetría y auditoría.
 * @returns {Promise<{success: boolean, isWinner?: boolean, couponCode?: string, verificationToken?: string, isRateLimited?: boolean, waitSeconds?: number, message?: string}>}
 */
const RecoverCoupon = async (query, searchType = null, sessionId = null) => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/recover-coupon`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query, searchType, sessionId })
        });
        const resData = await response.json().catch(() => ({}));
        if (response.status === 429) {
            return {
                success: false,
                isRateLimited: true,
                waitSeconds: resData.waitSeconds || 60,
                message: resData.message || 'Límite de consultas excedido. Por favor espera un momento antes de reintentar.'
            };
        }
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al recuperar cupón registrado.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en RecoverCoupon (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al recuperar cupón.' };
    }
};

/**
 * Registra eventos de telemetría del embudo de conversión (anonimizados sin PII).
 * POST /api/OptimoOpticas/track-metric
 * 
 * @async
 * @function TrackMetric
 * @param {string} eventName - Nombre del evento (ej: 'page_view', 'form_start', 'game_start', 'game_complete', etc.).
 * @param {string|null} [sessionId=null] - Identificador único de sesión.
 * @param {string|null} [source=null] - Origen del tráfico ('direct', 'qr_screen', etc.).
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const TrackMetric = async (eventName, sessionId = null, source = null) => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/track-metric`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ eventName, sessionId, source })
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: resData.message || 'Error al registrar métrica.' };
        }
        return resData.success !== undefined ? resData : { success: true };
    } catch (error) {
        console.warn("Fallo no bloqueante en TrackMetric (DL):", error);
        return { success: false, message: 'Error de conexión en telemetría.' };
    }
};

/**
 * Registra la captura de pantalla o descarga local del cupón digital realizada por el usuario.
 * POST /api/OptimoOpticas/log-screenshot
 * 
 * @async
 * @function LogScreenshot
 * @param {number|Object} participantId - ID del participante o payload de captura.
 * @param {string} [method='BUTTON_DOWNLOAD'] - Método detectado ('BUTTON_DOWNLOAD', 'KEYBOARD_SHORTCUT', 'HEURISTIC').
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const LogScreenshot = async (participantId, method = 'BUTTON_DOWNLOAD') => {
    try {
        const payload = typeof participantId === 'object' && participantId !== null
            ? participantId
            : { participantId, method };

        const response = await fetch(`${API_URL}OptimoOpticas/log-screenshot`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: resData.message || 'Error al registrar captura de pantalla.' };
        }
        return resData.success !== undefined ? resData : { success: true };
    } catch (error) {
        console.warn("Fallo no bloqueante en LogScreenshot (DL):", error);
        return { success: false, message: 'Error de conexión al registrar captura.' };
    }
};

// =============================================================================
// ENDPOINTS ADMINISTRATIVOS Y DE OPERACIÓN
// =============================================================================

/**
 * Obtiene la configuración completa para el panel de administración.
 * GET /api/OptimoOpticas/admin/config
 * 
 * @async
 * @function GetAdminConfig
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
const GetAdminConfig = async () => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/admin/config`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al obtener la configuración administrativa.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en GetAdminConfig (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al consultar configuración administrativa.' };
    }
};

/**
 * Actualiza la configuración de la dinámica desde el panel administrativo.
 * PUT /api/OptimoOpticas/admin/config
 * 
 * @async
 * @function UpdateAdminConfig
 * @param {Object} config - Configuración actualizada.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
const UpdateAdminConfig = async (config) => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/admin/config`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(config)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al actualizar la configuración.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en UpdateAdminConfig (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al actualizar configuración.' };
    }
};

/**
 * Obtiene el padrón de participantes con paginación, filtros y búsqueda.
 * GET /api/OptimoOpticas/admin/participants?page=...&pageSize=...&search=...&status=...
 * 
 * @async
 * @function GetAdminParticipants
 * @param {Object|string} [params={}] - Parámetros de consulta (page, pageSize, search, status, etc.).
 * @returns {Promise<{success: boolean, data?: Array<Object>|Object, total?: number, message?: string}>}
 */
const GetAdminParticipants = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params && typeof params === 'object') {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value);
                }
            });
        }
        const queryString = queryParams.toString();
        const endpoint = `${API_URL}OptimoOpticas/admin/participants${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(endpoint, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al obtener la lista de participantes.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en GetAdminParticipants (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al obtener participantes.' };
    }
};

/**
 * Obtiene las métricas y estadísticas de embudo de conversión en tiempo real.
 * GET /api/OptimoOpticas/admin/stats
 * 
 * @async
 * @function GetAdminStats
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
const GetAdminStats = async () => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/admin/stats`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al obtener estadísticas del embudo.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en GetAdminStats (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al obtener estadísticas.' };
    }
};

/**
 * Registra el canje físico o validación de un cupón en sucursal o estadio.
 * POST /api/OptimoOpticas/admin/redeem
 * 
 * @async
 * @function RedeemCoupon
 * @param {Object} payload - { couponCode, token, operator, branch }
 * @returns {Promise<{success: boolean, alreadyRedeemed?: boolean, message?: string, redeemedAtUtc?: string}>}
 */
const RedeemCoupon = async (payload) => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/admin/redeem`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(typeof payload === 'string' ? { couponCode: payload } : payload)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al canjear el cupón.',
                alreadyRedeemed: resData.alreadyRedeemed || false
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en RedeemCoupon (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al canjear cupón.' };
    }
};

/**
 * Descalifica a un participante por motivo fundado de auditoría u operación.
 * POST /api/OptimoOpticas/admin/disqualify
 * 
 * @async
 * @function DisqualifyParticipant
 * @param {number} participantId - ID numérico del participante.
 * @param {string} reason - Motivo de la descalificación.
 * @param {string} operator - Nombre o correo del operador actuante.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const DisqualifyParticipant = async (participantId, reason, operator) => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/admin/disqualify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ participantId, reason, operator })
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al descalificar al participante.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en DisqualifyParticipant (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al descalificar participante.' };
    }
};

/**
 * Restituye a un participante previamente descalificado.
 * POST /api/OptimoOpticas/admin/undisqualify
 * 
 * @async
 * @function UndisqualifyParticipant
 * @param {number} participantId - ID numérico del participante.
 * @param {string} operator - Nombre o correo del operador actuante.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const UndisqualifyParticipant = async (participantId, operator) => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/admin/undisqualify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ participantId, operator })
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al restituir al participante.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en UndisqualifyParticipant (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al restituir participante.' };
    }
};

/**
 * Reinicia el estado de la dinámica para una nueva jornada o partido.
 * POST /api/OptimoOpticas/admin/reset
 * 
 * @async
 * @function ResetDynamic
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const ResetDynamic = async () => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/admin/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al reiniciar la dinámica.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en ResetDynamic (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al reiniciar dinámica.' };
    }
};

/**
 * Actualiza la visibilidad de la portada / screen en tiempo real.
 * POST /api/OptimoOpticas/admin/screen-visibility
 * 
 * @async
 * @function SetScreenVisibility
 * @param {boolean} visible - Estado de visibilidad.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const SetScreenVisibility = async (visible) => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/admin/screen-visibility`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ visible })
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al actualizar visibilidad de pantalla.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en SetScreenVisibility (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al cambiar visibilidad de pantalla.' };
    }
};

/**
 * Retorna la URL directa del endpoint que genera y descarga el libro Excel (.xlsx) oficial.
 * 
 * @function GetExportExcelUrl
 * @returns {string} Endpoint URL para exportación ejecutiva en Excel.
 */
const GetExportExcelUrl = () => {
    return `${API_URL}OptimoOpticas/admin/export`;
};

// =============================================================================
// EXPORTACIÓN MODULAR (ES6+)
// =============================================================================

/**
 * Marca un cupón como descubierto (>65% rascado) en el backend.
 * POST /api/OptimoOpticas/mark-scratched
 * 
 * @async
 * @function MarkCouponScratched
 * @param {number|null} participantId - ID numérico del participante.
 * @param {string|null} [couponCode=null] - Folio alfanumérico del cupón.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const MarkCouponScratched = async (participantId, couponCode = null) => {
    try {
        const response = await fetch(`${API_URL}OptimoOpticas/mark-scratched`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ participantId: participantId || null, couponCode: couponCode || null })
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: resData.message || 'Error al marcar cupón como descubierto.' };
        }
        return resData.success !== undefined ? resData : { success: true };
    } catch (error) {
        console.warn("Fallo no bloqueante al marcar cupón como descubierto (DL):", error);
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Envía la señal para proyectar la portada de Optimo Ópticas en pantalla gigante (/screen Tile 49).
 * POST /api/Admin/sendQROptimoOpticas
 * 
 * @async
 * @function SendQROptimoOpticas
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const SendQROptimoOpticas = async () => {
    try {
        const response = await fetch(`${API_URL}Admin/sendQROptimoOpticas`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: resData.message || 'Error al proyectar portada en pantalla gigante.' };
        }
        return resData.success !== undefined ? resData : { success: true };
    } catch (error) {
        console.error("Error al proyectar en pantalla (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

export {
    GetPublicConfig,
    CheckDuplicate,
    SubmitLead,
    RecoverCoupon,
    TrackMetric,
    LogScreenshot,
    MarkCouponScratched,
    SendQROptimoOpticas,
    GetAdminConfig,
    UpdateAdminConfig,
    GetAdminParticipants,
    GetAdminStats,
    RedeemCoupon,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    ResetDynamic,
    SetScreenVisibility,
    GetExportExcelUrl
};

const DLOptimoOpticas = {
    GetPublicConfig,
    CheckDuplicate,
    SubmitLead,
    RecoverCoupon,
    TrackMetric,
    LogScreenshot,
    MarkCouponScratched,
    SendQROptimoOpticas,
    GetAdminConfig,
    UpdateAdminConfig,
    GetAdminParticipants,
    GetAdminStats,
    RedeemCoupon,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    ResetDynamic,
    SetScreenVisibility,
    GetExportExcelUrl
};

export default DLOptimoOpticas;

